import React,{useState, useEffect, useCallback} from 'react'
import { useNavigate, useSearchParams } from "react-router-dom";
import useFetch from '../../utils/useFetch';
import apiServer from '../../constants/apiServer';
import { del } from "../../utils/api";
import DataList from '../../common/DataList';
import defaultImg from '../../image/default-image.png'
import ConfirmationPopup from '../../common/ConfirmationPopup';
import { img_url_api } from "../../constants/apiImg";
import { Headers } from '../../constants/Headers';
import TableAction from '../../common/TableAction';
import { StatusCode } from "../../utils/commonFuncs";
import { useContext } from 'react';
import { DataContext } from '../../context/dataProvider';
import { useLocation } from 'react-router-dom';

const Process = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [headerInfo, setHeaderInfo] = useState(Headers['Process']);
  const [searchInput, setSearchInput] = useState("");

  // Get search term from URL
  const urlSearchTerm = searchParams.get('search') || '';
  const productId = searchParams.get('productId');

  // Initialize search input from URL
  useEffect(() => {
    setSearchInput(urlSearchTerm);
  }, [urlSearchTerm]);

  const { currentPageList, setCurrentPageList } = useContext(DataContext);

  const [ page, setPage ] = useState(currentPageList['process'] || 1);
  const [rowPerPage, setRowPerPage ] = useState(10);
  const [totalRecords, setTotalRecords ] = useState(0);
  const [actionStatus, setActionStatus] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [getListError, setGetListError ] = useState('');
  const [processId, setProcessId] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const apiUrl = urlSearchTerm 
    ? (productId 
      ? `${apiServer.process.list}?product_id=${productId}&page_at=${page}&row_count=${rowPerPage}&search=${encodeURIComponent(urlSearchTerm)}`
      : `${apiServer.process.list}?page_at=${page}&row_count=${rowPerPage}&search=${encodeURIComponent(urlSearchTerm)}`)
    : (productId 
      ? `${apiServer.process.list}?product_id=${productId}&page_at=${page}&row_count=${rowPerPage}`
      : `${apiServer.process.list}?page_at=${page}&row_count=${rowPerPage}`);


  const { data, totalCount, isLoading, error } = useFetch(apiUrl);

  const handleSearchSubmit = () => {
    // Update URL with search parameter
    const newParams = new URLSearchParams(searchParams);
    
    if (searchInput.trim()) {
        newParams.set('search', searchInput.trim());
    } else {
        newParams.delete('search');
    }
    
    // Reset to page 1 when searching
    newParams.set('page_at', '1');
    
    setSearchParams(newParams);
    setPage(1);
    setCurrentPageList(prev => ({ ...prev, process: 1 }));
  };

  const handleInputChange = (value) => {
    setSearchInput(value);
  };

  const modifiedData = useCallback((listData) => {
    const parseSafely = (data) => {
      if (typeof data !== 'string') return data;
      try {
        return JSON.parse(data);
      } catch (e) {
        return data;
      }
    };

    return listData.map((info, index) => {
      const titleData = parseSafely(info?.title);
      const descriptionData = parseSafely(info?.description);
      
      const processName = titleData?.en || titleData?.mm || titleData || 'N/A';
      const productName = info?.product?.name?.en || info?.product?.name?.mm || info?.product?.name || 'N/A';
      const firstMedia = info?.process_media?.[0];
      const description = descriptionData?.en || descriptionData?.mm || descriptionData || '';
      
      return {
        ...info,
        rowId: index + 1 + (page - 1) * rowPerPage,
        process_eng_name: processName,
        name: productName,
        images: firstMedia?.file || null,
        step: info?.step_name || '',
        description: description.length > 50 ? description.substring(0, 50) + '...' : description
      };
    });
  }, [page, rowPerPage]);

  const modifiedHeader = useCallback(() => {

    headerInfo[1]["renderCell"] = (params) => {
      const profile = params.formattedValue;
      const id = params.api.getRowIndex(params.row.id);
      const imageSrc = profile && profile !== "" && profile !== null 
        ? (profile.startsWith('http') ? profile : img_url_api + profile)
        : defaultImg;
      
      const fileExtension = profile?.split('.').pop()?.toLowerCase();
      const isVideo = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(fileExtension);
      
      return(
        isVideo ? (
          <video 
            src={imageSrc} 
            style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '50%' }}
            muted
          />
        ) : (
          <img src={imageSrc} alt="process-img" className={profile === "" || profile === undefined || profile === null ? "dummy_profile" : id === 0 ? "first--artist_profile" : "artist_profile"}/>
        )
      )
    };

    headerInfo[headerInfo.length - 1]["renderCell"] = ({id}) =>{
      const onEdit = () => {
          navigate(`${id}/edit`, { replace: true });
        };
        const onDelete = async () => {
          setProcessId(id);
          setConfirmDelete(true)
        };
      return (
        <TableAction onEdit={onEdit} onDelete={onDelete} />
      );
    };

    setHeaderInfo(headerInfo);

  }, [headerInfo, navigate])

  useEffect(()=>{
    setLoading(isLoading);
  },[isLoading])

  useEffect(() => {

    if(location?.state !== null){
      const { status, message } = location?.state;
      setActionStatus(status);
      setActionMessage(message);
      // Clear the location state after showing the alert
      window.history.replaceState({}, document.title, location.pathname);
    }

  }, [location]);

  useEffect(()=> {
    const processedData = modifiedData(data);
    setRowData(processedData);
  },[data, page, modifiedData]);

  useEffect(()=> {
    setGetListError(error)
  },[error])


  useEffect(() => {
    setTotalRecords(totalCount)
  },[totalCount])

  useEffect(() => {

    modifiedHeader();

  }, [headerInfo, navigate, modifiedHeader]);

  const handleConfirmDelete = async() => {
    setConfirmDelete(false)
    setLoading(true);
    try {

      const { data : deletedInfo } = await del(
        `${apiServer.process.delete}/${processId}`
      );

      setActionStatus(StatusCode[deletedInfo?.status]);
      setActionMessage(deletedInfo?.message);

      if(! (deletedInfo?.status >= 400 )){
        const dataList = rowData.filter(item => item.id !== processId);
        setRowData(dataList);
        setTotalRecords(totalRecords - 1);
        if(dataList.length === 0){

          setPage((prev) => prev > 1  ? prev - 1 : 1);
          setCurrentPageList((prev) => {
            return {
              ...currentPageList,
              "process" :  prev > 1 ? prev - 1 : 1
            }
          });
        }
      }

      setLoading(false);

    } catch (err) {
      
      setLoading(false);
      setActionStatus("error");
      setActionMessage(err.message);
    }
  }

  const handleChangePage = (currentPage) => {
    setPage(currentPage + 1)
    setCurrentPageList({
      ...currentPageList,
      "process" : currentPage + 1
    });
  }

  const handleChangePageSize = (rows) => {
    setRowPerPage(rows)
  }

  return (
    <>
      <DataList
        title={"Process List"}
        headers={headerInfo}
        rowData={rowData}
        isLoading={loading}
        totalCount={totalRecords}
        error={getListError}
        currentPage={page}
        pageSize={rowPerPage}
        onPageChange={handleChangePage}
        onPageSizeChange={handleChangePageSize}
        deleteMsg={actionMessage}
        deleteStatus={actionStatus}
        emptyDataText={'There is no process. Please Create One.'}
        onSearchChange={handleInputChange}
        onSearchSubmit={handleSearchSubmit}
        searchPlaceholder="Search processes..."
        searchIndicator={urlSearchTerm}
        onClearSearch={() => {
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('search');
          setSearchParams(newParams);
          setSearchInput('');
        }}
      />

      { confirmDelete && 
            <ConfirmationPopup
              headerText="Delete Process Step"
              body={"Are you sure you want to delete this process step ?"}
              onSubmit={handleConfirmDelete}
              onCancel={() => {
                setConfirmDelete(false)
              }}
          />
        }
    </>
  );
}

export default Process